import { useToken } from "@chakra-ui/react";
import { BBox2d } from "@turf/helpers/dist/js/lib/geojson";
import { bbox, featureCollection, point } from "@turf/turf";
import { distanceHelper } from "@ui/../common/src";
import { getHazardMapLegend } from "@ui/maps/hazard-map";
import type { Feature } from "geojson";
import type { MapLayerMouseEvent } from "mapbox-gl";
import { useCallback, useRef, useState } from "react";
import { LayerProps, MapRef } from "react-map-gl";
import { LatLonType } from "../types";
import type { FitBoundsOptions, LngLatBoundsLike } from "mapbox-gl";
import type { ViewState } from "react-map-gl";

export type InitialViewState = Partial<ViewState> & {
  bounds?: LngLatBoundsLike;
  fitBoundsOptions?: FitBoundsOptions;
};

export const getInitialViewState = (props: InitialViewState) => ({
  bearing: 0,
  pitch: 0,
  zoom: props.zoom || 3,
  ...props,
});

export interface FeatureLayers {
  category: string;
  features?: Feature[];
  layerProps?: LayerProps;
  displayName: string;
  color: string;
  singularName?: string;
}

export interface MapHoverState {
  id: string | number;
  source: string;
  lat: number;
  lon: number;
  distance: string;
}

export interface UseStaticMapOptions {
  userLocation: LatLonType;
  featureLayers: FeatureLayers[];
  distance: number;
  zoom?: number;
}

export function useStaticMap(options: UseStaticMapOptions) {
  const ref = useRef<MapRef | null>(null);

  const [hovered, setHovered] = useState<MapHoverState | null>(null);

  const colors = useToken(
    "colors",
    options.featureLayers?.map((x) => x.color) || ["blue.800"],
  );

  const legendRows =
    options.featureLayers?.[0].features &&
    options.featureLayers
      ?.map((x, i) =>
        getHazardMapLegend(
          x.displayName,
          x?.features?.[0] as any,
          colors[i],
        ).filter((x) => x.label !== "Queried Radius"),
      )

      .flat();

  const initialViewState = getInitialViewState({
    latitude: options.userLocation.lat,
    longitude: options.userLocation.lon,
    zoom: options?.zoom || 7,
  });

  const bounds =
    options.featureLayers?.[0]?.features &&
    options.featureLayers?.[0]?.features?.length > 0 &&
    (bbox(
      featureCollection([
        point([options.userLocation.lon, options.userLocation.lat]),
        ...options?.featureLayers?.[0]?.features,
      ]),
    ) as BBox2d);

  const resetViewState = () => {
    ref?.current && options?.zoom
      ? ref?.current?.easeTo({
          center: [options.userLocation.lon, options.userLocation.lat],
          zoom: options.zoom,
          duration: 500,
        })
      : bounds
      ? ref?.current?.fitBounds(bounds, {
          padding: 25,
          duration: 500,
        })
      : ref?.current?.easeTo({
          center: [options.userLocation.lon, options.userLocation.lat],
          zoom: options.zoom ?? 7,
          duration: 500,
        });
  };
  const onLoad = () => resetViewState();

  const findLayer = (source: string) => {
    return options?.featureLayers?.find((x) => x.category === source);
  };

  const onMouseEvent = useCallback(
    (event: MapLayerMouseEvent) => {
      const feature = event?.features?.[0];
      if (hovered?.id) {
        ref?.current?.setFeatureState(
          { source: hovered?.source, id: hovered?.id },
          { hover: false },
        );
        setHovered(null);
      }
      if (feature?.geometry && feature?.id) {
        const layer = findLayer(feature?.source);
        const distanceFromUserLocation = distanceHelper(
          [options.userLocation.lon, options.userLocation.lat],
          feature.geometry as any,
        );
        setHovered({
          id: feature?.id,
          source: feature?.source,
          lat: event.lngLat.lat,
          lon: event.lngLat.lng,
          distance: `${
            layer?.singularName ? layer.singularName + " " : ""
          }${distanceFromUserLocation.toFixed(2)} Miles Away`,
        });
        ref?.current?.setFeatureState(
          {
            source: feature?.source,
            id: feature?.id,
          },
          { hover: true },
        );
      }
    },
    [hovered],
  );

  return {
    ref,
    onLoad,
    resetViewState,
    initialViewState,
    onMouseEvent,
    hovered,
    legendRows,
    colors,
  };
}
